<template>
  <div
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDragLeave"
  >
    <div class="note-new-input">
      <v-textarea
        ref="noteTextarea"
        v-model="newNote"
        :rows="3"
        :placeholder="placeholder"
        variant="outlined"
        density="compact"
        hide-details
        @input="updateMentionQuery"
        @click="updateMentionQuery"
        @keydown="onMentionKeydown"
        @blur="closeMentionMenu"
      ></v-textarea>
      <!-- @ picker, mousedown so it fires before the textarea blur -->
      <v-card
        v-if="enableMentions && mentionQuery !== undefined && mentionMatches.length"
        class="note-mention-menu"
        elevation="4"
      >
        <v-list density="compact">
          <v-list-item
            v-for="(agent, index) in mentionMatches"
            :key="agent.id"
            :active="index === mentionIndex"
            @mousedown.prevent="selectMention(agent)"
          >
            <v-list-item-title>
              {{ agent.first_name }} {{ agent.last_name }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </div>
    <!-- New file upload on new note, only shows when drag is initiated or when config enables it. -->
    <asset-inline-upload
      v-if="showUploadOnNewNotes"
      v-show="showFileUploadOnHover ? isDrag : true"
      :asset-types="['image', 'pdf', 'video', 'audio']"
      @upload-complete="handleAssetAdd"
      @upload-error="handleUploadError"
    ></asset-inline-upload>

    <div v-if="useCompactButtons" class="note-new-compact-container">
      <div class="note-new-button-group">
        <v-tooltip text="Upload file" location="top">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              size="small"
              @click="showAssetUpload = !showAssetUpload"
              variant="text"
            >
              <v-icon>mdi-upload</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <content-block-select
          v-if="showQuickReply"
          :use-menu="true"
          :replyType="'text'"
          @updateContent="mergeMessage"
          :size="'small'"
          :use-vuetify="true"
        ></content-block-select>
        <v-tooltip text="Quick reply" location="top">
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="getTextBlocks.length"
              v-bind="props"
              size="small"
              @click="showQuickReply = !showQuickReply"
              variant="text"
            >
              <v-icon>mdi-message-reply-text</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </div>
      <v-btn
        size="small"
        variant="tonal"
        color="primary"
        @click="saveNewNotes"
        :icon="true"
        :disabled="!newNote || !newNote.length"
      >
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </div>

    <v-row v-else justify="end" align="center" class="mt-3 mb-2 mr-1">
      <content-block-select
        v-if="showQuickReply"
        :use-menu="true"
        :replyType="'text'"
        :size="'small'"
        @updateContent="mergeMessage"
      >
      </content-block-select>
      <v-btn
        v-if="getTextBlocks.length"
        size="small"
        @click="showQuickReply = !showQuickReply"
      >
        <v-icon>mdi-comment</v-icon>
        Quick Reply
      </v-btn>

      <template v-if="showCancel">
        <v-divider vertical></v-divider>
        <v-btn size="small" @click="cancelNote" variant="text"> Cancel </v-btn>
      </template>

      <v-divider vertical></v-divider>

      <v-btn size="small" @click="showAssetUpload = !showAssetUpload">
        <v-icon>mdi-upload</v-icon>
        Upload Asset
      </v-btn>
      <v-btn size="small" @click="saveNewNotes" class="ml-2"> Save </v-btn>
    </v-row>
    <assets-grid
      v-if="attachFiles.length > 0"
      :assets="attachFiles"
      :allow-delete="true"
      @delete-asset="(asset) => handleNewAssetDelete(asset)"
    ></assets-grid>
    <el-dialog :title="'Select File'" v-model="showAssetUpload">
      <asset-library
        :asset-types="['image', 'pdf', 'audio', 'video']"
        @update="handleAssetAdd"
        @close="showAssetUpload = false"
        :thumbnail-mode="true"
        :limit="5"
      ></asset-library>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { mapGetters } from 'vuex';
import { uploadAsset } from 'src/util/assetUtils.js';

const emit = defineEmits(['newNote']);

import AssetLibrary from 'src/components/UIComponents/AssetLibrary';
import AssetsGrid from 'src/components/Dashboard/Views/Assets/AssetsGrid.vue';
import AssetInlineUpload from 'src/components/UIComponents/AssetInlineUpload.vue';
import ContentBlockSelect from 'src/components/UIComponents/ContentBlockSelect';

import notesServices from 'src/services/notesServices';
import templateService from 'src/services/templateServices';
import SmsService from 'src/services/smsServices.js';

const store = useStore();

// computed
const getTextBlocks = computed(
  () => store.getters['contentBlocks/getTextBlocks']
);

const getAppConfig = computed(() => store.getters['appConfig/getAppConfig']);

const showUploadOnNewNotes = computed(() => {
  return getAppConfig.value?.notes?.showUploadOnNewNotes ?? false;
});

//data

const newNote = ref(undefined);
const attachFiles = ref([]);
const isDrag = ref(false);
const showQuickReply = ref(false);
const showAssetUpload = ref(false);
const loading = ref(true);
// @ picker
const MENTION_MENU_SIZE = 6;
const noteTextarea = ref(null);
const mentionQuery = ref(undefined); // text after @, undefined when closed
const mentionStart = ref(0); // index of the @ in newNote
const mentionIndex = ref(0); // highlighted menu row
const mentions = ref([]); // { id, label } picked while composing

const props = defineProps({
  // Below are provided for contact merge reasons.
  // Explicit or one object??
  contactId: {
    type: Number,
    required: false,
  },
  leadId: {
    type: Number,
    required: false,
  },
  // Task not supported on merge yet
  taskId: {
    type: Number,
    required: false,
  },
  reservationId: {
    type: Number,
    required: false,
  },
  showFileUploadOnHover: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Add a note...',
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  useCompactButtons: {
    type: Boolean,
    default: false,
  },
  // @user picker. The saved note carries the mentioned user ids so the
  // parent can alert them. Only conversation notes support it for now.
  enableMentions: {
    type: Boolean,
    default: false,
  },
});

const mentionMatches = computed(() => {
  if (mentionQuery.value === undefined) {
    return [];
  }
  const q = mentionQuery.value.toLowerCase();
  const myId = store.getters['auth/getLoginAccount']?.userId;
  return (store.getters['agents/getAgents'] ?? [])
    .filter((a) => a.id != myId)
    .filter((a) => {
      const first = (a.first_name ?? '').toLowerCase();
      const last = (a.last_name ?? '').toLowerCase();
      return (
        first.startsWith(q) || last.startsWith(q) || (first + last).startsWith(q)
      );
    })
    .slice(0, MENTION_MENU_SIZE);
});

if (props.enableMentions) {
  store.dispatch('agents/fetchAgents');
}

// Methods
const getTextareaEl = () =>
  noteTextarea.value?.$el?.querySelector('textarea') ?? undefined;

// Open the picker when the caret sits right after an @word
const updateMentionQuery = () => {
  if (!props.enableMentions) {
    return;
  }
  const el = getTextareaEl();
  const text = newNote.value ?? '';
  const caret = el?.selectionStart ?? text.length;
  const match = /(^|\s)@([\w.'-]*)$/.exec(text.slice(0, caret));
  if (!match) {
    closeMentionMenu();
    return;
  }
  mentionStart.value = caret - match[2].length - 1;
  if (mentionQuery.value !== match[2]) {
    mentionIndex.value = 0;
  }
  mentionQuery.value = match[2];
};

const closeMentionMenu = () => {
  mentionQuery.value = undefined;
  mentionIndex.value = 0;
};

const selectMention = (agent) => {
  const label = `@${[agent.first_name, agent.last_name]
    .filter(Boolean)
    .join(' ')}`;
  const text = newNote.value ?? '';
  const end = mentionStart.value + 1 + (mentionQuery.value ?? '').length;
  const rest = text.slice(end);
  const before =
    text.slice(0, mentionStart.value) + label + (/^\s/.test(rest) ? '' : ' ');
  newNote.value = before + rest;
  if (!mentions.value.some((m) => m.id == agent.id)) {
    mentions.value.push({ id: agent.id, label: label });
  }
  closeMentionMenu();
  // Put the caret after the inserted name
  const el = getTextareaEl();
  if (el) {
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(before.length, before.length);
    });
  }
};

const onMentionKeydown = (e) => {
  if (mentionQuery.value === undefined || !mentionMatches.value.length) {
    return;
  }
  const count = mentionMatches.value.length;
  switch (e.key) {
    case 'ArrowDown':
      mentionIndex.value = (mentionIndex.value + 1) % count;
      break;
    case 'ArrowUp':
      mentionIndex.value = (mentionIndex.value - 1 + count) % count;
      break;
    case 'Enter':
    case 'Tab':
      selectMention(mentionMatches.value[mentionIndex.value]);
      break;
    case 'Escape':
      closeMentionMenu();
      break;
    default:
      return;
  }
  e.preventDefault();
};

// Only users whose @Name is still in the text, in case it was edited out
const currentMentionIds = () =>
  mentions.value
    .filter((m) => (newNote.value ?? '').includes(m.label))
    .map((m) => m.id);

const handleDragEnter = () => {
  if (props.showFileUploadOnHover) {
    isDrag.value = true;
  }
};

const handleDragLeave = () => {
  if (props.showFileUploadOnHover) {
    isDrag.value = false;
  }
};

const handleAssetAdd = async (asset) => {
  if (!asset?.url) {
    return;
  }

  attachFiles.value.push(asset);
  isDrag.value = false;
};
const handleUploadError = (error) => {
  console.error('Upload failed:', error);
};

const mergeMessage = async (content, edit = false) => {
  let result;
  try {
    result = await templateService.getTemplateMerge(
      0,
      props.contactId,
      undefined, // campaignId
      props.reservationId, // reservationId
      props.leadId,
      content
    );
  } catch (e) {
    if (!e.response) {
      throw e;
    }
    if (e.response.status == 422) {
      const result = e.response.data;
      this.$message({
        type: 'error',
        message:
          'Error Parsing Template at this line: <br><br>' +
            result.friendly_error ?? result.status,
        showClose: true,
        // Love me some danger
        dangerouslyUseHTMLString: true,
      });
    } else {
      this.$message({
        type: 'error',
        message: 'Unexpected Error Parsing Template',
        showClose: true,
      });
    }
    return false;
  }

  newNote.value = String(newNote.value).concat(result.data.rich_content);
};

const saveNewNotes = async () => {
  emit('newNote', {
    note: newNote.value,
    attachments: attachFiles.value,
    mentions: props.enableMentions ? currentMentionIds() : [],
  });

  // clear it out
  newNote.value = '';
  mentions.value = [];
  closeMentionMenu();
};

const cancelNote = () => {
  emit('newNote', undefined);
};

// watch must be last
watch(
  () => props.messageId,
  (newId) => {
    // loadMsg();
  },
  { immediate: true }
);
</script>

<style>
.note-new-input {
  position: relative;
}

.note-mention-menu {
  position: absolute;
  left: 8px;
  bottom: 100%; /* opens upward, the composer sits at the bottom of the pane */
  z-index: 10;
  min-width: 200px;
  max-width: calc(100% - 16px);
}

.note-new-compact-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* margin-top: 8px; */
}

.note-new-button-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
